import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardHat, Lock, Mail } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login fallito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* left branding */}
      <div className="hidden lg:flex brand-navy text-white relative">
        <div className="m-auto max-w-md px-10">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <HardHat className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <div className="text-3xl font-extrabold tracking-tight">M. Biella</div>
              <div className="text-[11px] tracking-[0.25em] text-amber-400 font-semibold">WORK MANAGER</div>
            </div>
          </div>
          <h1 className="text-4xl font-extrabold leading-tight mb-4">
            Gestione cantieri impiantistica industriale.
          </h1>
          <p className="text-slate-300 leading-relaxed">
            Monitora ore, avanzamento e documentazione di ogni progetto in un unico posto.
            Semplice, veloce, mobile friendly.
          </p>

          <div className="mt-14 grid grid-cols-3 gap-4 text-center">
            <div className="rounded-xl bg-white/5 p-4">
              <div className="text-2xl font-bold text-amber-400">Ore</div>
              <div className="text-xs text-slate-300 mt-1">Tracciamento giornaliero</div>
            </div>
            <div className="rounded-xl bg-white/5 p-4">
              <div className="text-2xl font-bold text-emerald-400">Stato</div>
              <div className="text-xs text-slate-300 mt-1">Attivi &amp; Conclusi</div>
            </div>
            <div className="rounded-xl bg-white/5 p-4">
              <div className="text-2xl font-bold text-sky-400">File</div>
              <div className="text-xs text-slate-300 mt-1">Foto, PDF, schemi</div>
            </div>
          </div>
        </div>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl brand-navy flex items-center justify-center">
              <HardHat className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <div className="text-xl font-extrabold text-brand-navy">M. Biella</div>
              <div className="text-[10px] tracking-[0.25em] text-amber-600 font-semibold">WORK MANAGER</div>
            </div>
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Bentornato</h2>
          <p className="text-slate-500 mb-8 text-sm">Accedi con le tue credenziali per continuare.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@mbiella.it"
                  className="pl-9 h-11"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-semibold text-slate-700">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 h-11"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 brand-navy hover:opacity-90 text-white font-semibold"
              data-testid="login-submit-button"
            >
              {loading ? "Accesso in corso..." : "Accedi"}
            </Button>
          </form>

          <div className="mt-8 text-xs text-slate-400 text-center">
            Account demo: <span className="text-slate-600 font-medium">admin@mbiella.it / admin123</span>
          </div>
        </div>
      </div>
    </div>
  );
}
